import api from './api';

export interface PaymentWebhookData {
    bookingRef: string;
    status: string;
    paymentId?: string;
    transactionId?: string;
    amount?: string;
    currency?: string;
}

export interface PaymentWebhookResponse {
    success: boolean;
    message: string;
    data?: {
        bookingId?: string;
        updatedStatus?: string;
        confirmationEmail?: boolean;
    };
}

// Simple in-memory cache to prevent duplicate webhook calls
const webhookCache = new Map<string, { timestamp: number; response: PaymentWebhookResponse }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const paymentWebhookService = {
    // Send payment completion webhook
    sendPaymentWebhook: async (webhookData: PaymentWebhookData): Promise<PaymentWebhookResponse> => {
        try {
            // Check cache for recent webhook with same booking ref
            const cacheKey = `${webhookData.bookingRef}-${webhookData.status}`;
            const cached = webhookCache.get(cacheKey);
            
            if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
                console.log('🔄 Using cached webhook response for:', cacheKey);
                return cached.response;
            }

            console.log('🌐 Sending webhook to backend:', webhookData);
            const response = await api.post('/booking/webhook/payment', webhookData);
            
            // Cache the successful response
            webhookCache.set(cacheKey, {
                timestamp: Date.now(),
                response: response.data
            });
            
            return response.data;
        } catch (error) {
            console.error('Payment webhook failed:', error);
            throw error;
        }
    },

    // Verify payment status
    verifyPaymentStatus: async (bookingRef: string): Promise<PaymentWebhookResponse> => {
        try {
            const response = await api.get(`/booking/webhook/verify/${bookingRef}`);
            return response.data;
        } catch (error) {
            console.error('Payment verification failed:', error);
            throw error;
        }
    },

    // Clear webhook cache (useful for testing)
    clearCache: () => {
        webhookCache.clear();
        console.log('🧹 Webhook cache cleared');
    }
};